import { useState, useEffect } from "react";
import { motion, AnimatePresence, animate, useReducedMotion } from "framer-motion";
import { CalendarDays, MapPin, Play, X, Backpack, Car, CheckCircle2 } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import GlowButton from "./GlowButton";
import content from "../content";

export default function FestivalSection() {
  const { festival } = content;
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(false);

  const bar = festival.donationBar;
  const percent = bar ? Math.round((bar.donated / bar.goal) * 100) : 0;
  const [count, setCount] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (festival.hidden) return null;

  return (
    <section
      id="festival"
      className="relative w-full py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: "#066DF1" }}
      data-testid="festival-section"
    >
      {/* Faded, muted video backdrop */}
      {!reduced && (
        <video
          className="pointer-events-none absolute inset-0 w-full h-full object-cover opacity-[0.22]"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src={festival.videoMp4} type="video/mp4" />
          {festival.videoWebm && <source src={festival.videoWebm} type="video/webm" />}
        </video>
      )}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(circle at 82% 12%, rgba(255,255,255,0.2), transparent 45%)" }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: logo + details */}
        <Reveal className="text-center lg:text-left">
          <Rise>
            <img
              src={festival.logo}
              alt="Festival de Verano 2026 — Iglesia Puerta del Cielo"
              className="w-full max-w-md mx-auto lg:mx-0 mb-8 drop-shadow-[0_14px_36px_rgba(0,0,0,0.28)]"
              data-testid="festival-logo"
            />
          </Rise>

          <Rise>
            <div className="flex flex-wrap justify-center lg:justify-start items-center gap-2.5 mb-7">
              {festival.activities.map((a, i) => (
                <span
                  key={a}
                  className="inline-flex items-center rounded-full border border-white/35 bg-white/15 backdrop-blur-sm px-4 py-2 font-poppins text-xs md:text-sm text-white"
                  data-testid={`festival-activity-${i}`}
                >
                  {a}
                </span>
              ))}
            </div>
          </Rise>

          {festival.whenBadge && (
            <Rise>
              <div className="flex justify-center lg:justify-start mb-5">
                <motion.span
                  animate={reduced ? {} : { scale: [1, 1.05, 1] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  className="relative inline-flex items-center gap-2.5 rounded-full bg-white px-6 py-3 font-anton uppercase tracking-wide text-[#066DF1] text-xl md:text-2xl shadow-[0_12px_30px_-8px_rgba(0,0,0,0.55)]"
                  data-testid="festival-when-badge"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-[#066DF1] animate-pulse" />
                  {festival.whenBadge}
                </motion.span>
              </div>
            </Rise>
          )}

          <Rise>
            <div className="flex flex-wrap justify-center lg:justify-start items-center gap-3 mb-8">
              <span className="inline-flex items-center gap-2.5 rounded-full bg-white px-5 py-2.5 font-poppins text-sm font-bold text-[#066DF1]">
                <CalendarDays size={16} />
                {festival.date}
              </span>
              <span className="inline-flex items-center gap-2.5 rounded-full border border-white/35 bg-white/10 px-5 py-2.5 font-poppins text-sm font-medium text-white">
                <MapPin size={16} />
                {festival.place}
              </span>
            </div>
          </Rise>

          <Rise>
            <p className="font-poppins text-white/90 text-base md:text-lg leading-relaxed mb-9 max-w-xl mx-auto lg:mx-0">
              {festival.text}
            </p>
          </Rise>

          {/* Barra de donaciones interactiva */}
          {bar && (
            <Rise>
              <motion.div
                onViewportEnter={() =>
                  animate(0, percent, {
                    duration: 1.4,
                    ease: [0.22, 1, 0.36, 1],
                    onUpdate: (v) => setCount(Math.round(v)),
                  })
                }
                viewport={{ once: true, amount: 0.5 }}
                className="group mb-9 max-w-xl mx-auto lg:mx-0 rounded-2xl border border-white/25 bg-white/10 backdrop-blur-sm p-5 transition-colors duration-300 hover:border-white/50"
                data-testid="festival-donation-bar"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <span className="inline-flex items-center gap-2 font-poppins text-sm font-semibold text-white">
                    <Backpack size={17} /> {bar.label}
                  </span>
                  <span
                    className="font-anton text-white text-2xl leading-none"
                    data-testid="festival-donation-percent"
                  >
                    {count}%
                  </span>
                </div>
                <div className="relative h-3.5 rounded-full bg-white/20 overflow-hidden">
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full bg-white"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${percent}%` }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <span className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white shadow-[0_0_14px_rgba(255,255,255,0.95)]" />
                  </motion.div>
                </div>
                <p className="mt-2.5 font-poppins text-xs md:text-sm text-white/90">
                  <span className="font-bold">{bar.donated}</span> de {bar.goal} {bar.unit}
                </p>
                {bar.note && (
                  <p className="mt-2 inline-flex items-center gap-2 font-poppins text-sm font-semibold text-white" data-testid="festival-donation-note">
                    <CheckCircle2 size={16} /> {bar.note}
                  </p>
                )}
              </motion.div>
            </Rise>
          )}

          {festival.parking && (
            <Rise>
              <div
                className="mb-9 max-w-xl mx-auto lg:mx-0 rounded-2xl border border-white/25 bg-white/10 backdrop-blur-sm p-5"
                data-testid="festival-parking"
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <Car size={18} className="text-white" />
                  <span className="font-anton uppercase tracking-wide text-white text-lg">Estacionamiento</span>
                </div>
                <p className="font-poppins text-sm md:text-base text-white/90 leading-relaxed mb-3.5">
                  {festival.parking.note}
                </p>
                <div className="rounded-xl bg-white/15 border border-white/25 p-4">
                  <p className="font-poppins text-[11px] uppercase tracking-[0.2em] text-white/80 mb-1.5">
                    {festival.parking.overflowLabel}
                  </p>
                  <p className="font-poppins text-white font-bold leading-snug">
                    {festival.parking.overflowAddress}
                  </p>
                  <p className="font-poppins text-sm text-white/85 mt-0.5">
                    {festival.parking.overflowDistance}
                  </p>
                  {festival.parking.mapHref && (
                    <a
                      href={festival.parking.mapHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 font-poppins text-sm font-semibold text-white underline underline-offset-4 hover:text-white/80 transition-colors"
                      data-testid="festival-parking-map"
                    >
                      <MapPin size={15} /> Cómo llegar
                    </a>
                  )}
                </div>
              </div>
            </Rise>
          )}

          <Rise>
            <GlowButton href={festival.cta.href} variant="white" testId="festival-cta">
              {festival.cta.label}
            </GlowButton>
          </Rise>
        </Reveal>

        {/* Right: expandable video player */}
        <Reveal className="relative">
          <Rise className="relative">
            <div className="absolute -inset-5 rounded-[2.75rem] bg-white/15 blur-3xl" aria-hidden="true" />
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Ver el video del Festival de Verano"
              data-testid="festival-video-trigger"
              className="group relative block w-full max-w-[330px] mx-auto aspect-[9/16] rounded-[2rem] overflow-hidden border-4 border-white/70 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)] cursor-pointer"
            >
              <video
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay={!reduced}
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              >
                <source src={festival.videoMp4} type="video/mp4" />
                {festival.videoWebm && <source src={festival.videoWebm} type="video/webm" />}
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-[70px] h-[70px] rounded-full bg-white/90 text-[#066DF1] shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-transform duration-300 group-hover:scale-110">
                  <Play size={30} className="ml-1" fill="currentColor" />
                </span>
              </span>
              <span className="absolute bottom-4 left-0 right-0 text-center font-poppins text-xs uppercase tracking-[0.25em] text-white/90">
                Toca para ver
              </span>
            </button>
          </Rise>
        </Reveal>
      </div>

      {/* Lightbox video */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
            data-testid="festival-lightbox"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar video"
              data-testid="festival-lightbox-close"
              className="absolute top-5 right-5 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white hover:text-[#066DF1] transition-colors duration-300"
            >
              <X size={24} />
            </button>
            <motion.video
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-[92vw] md:max-w-[420px] w-auto rounded-2xl shadow-[0_30px_90px_-20px_rgba(0,0,0,0.9)] border border-white/10"
              controls
              playsInline
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              <source src={festival.videoMp4} type="video/mp4" />
              {festival.videoWebm && <source src={festival.videoWebm} type="video/webm" />}
            </motion.video>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
